import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useApp } from "@/contexts/AppContext";
import { toast } from "sonner";
import { Plus, Trash2, ArrowRight, Building2, Wrench, ChevronRight } from "lucide-react";
import BiSelect from "@/components/BiSelect";

const TRADES = ["General Builder", "Carpenter", "Electrician", "Plumber", "Painter", "Concreter", "Landscaper", "Civil Contractor", "Roofer", "Tiler", "Other"];
const jobUid = () => (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export default function Onboarding() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const { reloadJobs } = useApp();
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState({ business_name: "", abn: "", trade_type: "General Builder", default_gst: true });
  const [jobs, setJobs] = useState([{ _id: jobUid(), name: "", client: "", budget: 0 }]);
  const [busy, setBusy] = useState(false);

  const addJob = () => setJobs((j) => [...j, { _id: jobUid(), name: "", client: "", budget: 0 }]);
  const removeJob = (id) => setJobs((j) => j.filter((jj) => jj._id !== id));
  const setJob = (id, k, v) => setJobs((j) => j.map((jj) => jj._id === id ? { ...jj, [k]: v } : jj));

  const finish = async () => {
    setBusy(true);
    try {
      await api.put("/profile", profile);
      const validJobs = jobs.filter((j) => j.name.trim());
      for (const j of validJobs) {
        await api.post("/jobs", { ...j, budget: Number(j.budget) || 0 });
      }
      await api.post("/auth/complete-onboarding");
      await refresh();
      await reloadJobs();
      toast.success("You're all set!");
      nav("/");
    } catch (e) {
      toast.error("Setup failed, please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1F1F1F] bi-grain flex items-start justify-center py-8 px-4">
      <div className="w-full max-w-lg relative z-10">
        <div className="flex items-center gap-2 mb-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`h-1 flex-1 rounded-full ${step >= n ? "bg-[#FF9500]" : "bg-white/10"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="bi-surface p-6">
            <div className="flex items-center gap-2 text-[#FF9500] mb-1"><Building2 className="w-5 h-5" /><span className="text-xs uppercase tracking-wider font-heading">Step 1 · Business</span></div>
            <h1 className="font-heading font-extrabold text-2xl">Tell us about your business</h1>
            <p className="text-white/60 text-sm mt-1">We use this on exports and BAS drafts.</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/60">Business name</label>
                <input data-testid="ob-biz-name" className="mt-1 w-full bi-input" value={profile.business_name} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} placeholder="e.g. Bluey Builders Pty Ltd" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/60">ABN</label>
                <input data-testid="ob-abn" className="mt-1 w-full bi-input" value={profile.abn} onChange={(e) => setProfile({ ...profile, abn: e.target.value })} placeholder="11 digit ABN" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/60">Trade</label>
                <BiSelect data-testid="ob-trade" className="mt-1 w-full bi-input" value={profile.trade_type} onChange={(e) => setProfile({ ...profile, trade_type: e.target.value })}>
                  {TRADES.map((t) => <option key={t}>{t}</option>)}
                </BiSelect>
              </div>
              <label className="flex items-center gap-3 h-14 px-3 bi-surface-2 cursor-pointer">
                <input type="checkbox" checked={profile.default_gst} onChange={(e) => setProfile({ ...profile, default_gst: e.target.checked })} className="w-5 h-5 accent-[#FF9500]" data-testid="ob-gst" />
                <span>I&apos;m registered for GST (auto 1/11th)</span>
              </label>
            </div>
            <button data-testid="ob-next-1" onClick={() => setStep(2)} className="mt-6 w-full h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2">Continue <ArrowRight className="w-5 h-5" /></button>
          </div>
        )}

        {step === 2 && (
          <div className="bi-surface p-6">
            <div className="flex items-center gap-2 text-[#FF9500] mb-1"><Wrench className="w-5 h-5" /><span className="text-xs uppercase tracking-wider font-heading">Step 2 · Jobs</span></div>
            <h1 className="font-heading font-extrabold text-2xl">Add your current jobs</h1>
            <p className="text-white/60 text-sm mt-1">You can edit these anytime.</p>
            <div className="mt-5 space-y-3">
              {jobs.map((j, i) => (
                <div key={j._id} className="bi-surface-2 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input data-testid={`ob-job-name-${i}`} className="flex-1 bi-input" value={j.name} onChange={(e) => setJob(j._id, "name", e.target.value)} placeholder="Job name (e.g. Smith Reno)" />
                    <button data-testid={`ob-job-remove-${i}`} onClick={() => removeJob(j._id)} disabled={jobs.length === 1} className="w-14 h-14 rounded-xl bi-ghost-btn flex items-center justify-center disabled:opacity-40"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input className="bi-input" value={j.client} onChange={(e) => setJob(j._id, "client", e.target.value)} placeholder="Client" />
                    <input className="bi-input" type="number" value={j.budget} onChange={(e) => setJob(j._id, "budget", e.target.value)} placeholder="Budget $" />
                  </div>
                </div>
              ))}
              <button data-testid="ob-job-add" onClick={addJob} className="w-full h-14 rounded-xl bi-ghost-btn flex items-center justify-center gap-2"><Plus className="w-4 h-4" /> Add another job</button>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-2">
              <button onClick={() => setStep(1)} className="h-14 rounded-xl bi-ghost-btn">Back</button>
              <button data-testid="ob-next-2" onClick={() => setStep(3)} className="h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2">Next <ArrowRight className="w-5 h-5" /></button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bi-surface p-6">
            <div className="flex items-center gap-2 text-[#FF9500] mb-1"><ChevronRight className="w-5 h-5" /><span className="text-xs uppercase tracking-wider font-heading">Step 3 · Confirm</span></div>
            <h1 className="font-heading font-extrabold text-2xl">You&apos;re ready to go</h1>
            <p className="text-white/60 text-sm mt-1">We&apos;ve set up 8 default deduction categories for you.</p>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between bi-surface-2 px-3 py-2 rounded-lg"><span className="text-white/60">Business</span><span>{profile.business_name || "—"}</span></div>
              <div className="flex justify-between bi-surface-2 px-3 py-2 rounded-lg"><span className="text-white/60">Trade</span><span>{profile.trade_type}</span></div>
              <div className="flex justify-between bi-surface-2 px-3 py-2 rounded-lg"><span className="text-white/60">Jobs added</span><span>{jobs.filter((j) => j.name).length}</span></div>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-2">
              <button onClick={() => setStep(2)} className="h-14 rounded-xl bi-ghost-btn">Back</button>
              <button data-testid="ob-finish" onClick={finish} disabled={busy} className="h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2 disabled:opacity-60">{busy ? "Setting up…" : "Start using BuildIntel"} <ArrowRight className="w-5 h-5" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
